import React, { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import { useParams, Link as RouterLink } from "react-router-dom";
import { changePassword } from "./api-auth.js";
import { findByHostname } from "../components/Instituicao/api-instituicao";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AuthLayout, { AuthFormHeading } from "./AuthLayout";

// Mapeia trechos da mensagem de erro da API para uma mensagem amigável.
const ERROR_MESSAGES = [
  ["Already used", "Link já utilizado."],
  [
    "Limit Time expired",
    "Tempo limite para resetar a senha expirado. Refaça o processo para resetar a senha.",
  ],
];

const GENERIC_ERROR = "Não foi possível alterar a senha. Tente novamente.";

function resolveErrorMessage(apiMessage) {
  const match = ERROR_MESSAGES.find(([needle]) => apiMessage?.includes(needle));
  return match ? match[1] : GENERIC_ERROR;
}

export default function ResetPasswordV2() {
  const { codResetPassword } = useParams();
  const [passwords, setPasswords] = useState({ password1: "", password2: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [instituicao, setInstituicao] = useState(null);
  const [feedback, setFeedback] = useState(null); // { severity: "success" | "error", message }
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    document.title = "Resetar Senha";
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    findByHostname(window.location.href, { signal: controller.signal })
      .then((data) => {
        if (data && !data.error) setInstituicao(data);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, []);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const clickSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (isLoading || passwordChanged) return; // evita duplo envio

      if (!passwords.password1 || !passwords.password2) {
        setFeedback({
          severity: "error",
          message: "Preencha os dois campos de senha.",
        });
        return;
      }

      if (passwords.password1 !== passwords.password2) {
        setFeedback({
          severity: "error",
          message: "Digite a mesma senha para os dois campos de senha.",
        });
        return;
      }

      setIsLoading(true);
      setFeedback(null);
      try {
        const data = await changePassword({
          ...passwords,
          codigoResetPassword: codResetPassword,
        });

        if (!data || data.error) {
          console.error(data);
          setFeedback({
            severity: "error",
            message: resolveErrorMessage(data?.message),
          });
        } else {
          setFeedback({
            severity: "success",
            message: "Senha alterada com sucesso.",
          });
          setPasswordChanged(true);
        }
      } catch (err) {
        console.error(err);
        setFeedback({ severity: "error", message: GENERIC_ERROR });
      } finally {
        setIsLoading(false);
      }
    },
    [passwords, codResetPassword, isLoading, passwordChanged],
  );

  const fieldsDisabled = isLoading || passwordChanged;
  const mismatch =
    passwords.password2.length > 0 &&
    passwords.password1 !== passwords.password2;

  const passwordInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <LockOutlinedIcon fontSize="small" color="action" />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          onClick={toggleShowPassword}
          edge="end"
          size="small"
          disabled={fieldsDisabled}
        >
          {showPassword ? (
            <VisibilityOffOutlinedIcon fontSize="small" />
          ) : (
            <VisibilityOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <AuthLayout
      eyebrow={instituicao?.nome || "Portal Acadêmico"}
      headline="Crie uma nova senha para voltar a acessar."
      logoUrl={instituicao?.urlLogo}
    >
      <AuthFormHeading
        title="Resetar senha"
        subtitle="Digite e confirme a sua nova senha."
      />

      <form onSubmit={clickSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          name="password1"
          label="Nova senha"
          type={showPassword ? "text" : "password"}
          id="password1"
          autoComplete="new-password"
          autoFocus
          disabled={fieldsDisabled}
          onChange={handleChange}
          value={passwords.password1}
          InputProps={passwordInputProps}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password2"
          label="Confirmar senha"
          type={showPassword ? "text" : "password"}
          id="password2"
          autoComplete="new-password"
          disabled={fieldsDisabled}
          onChange={handleChange}
          value={passwords.password2}
          error={mismatch}
          helperText={mismatch ? "As senhas não coincidem." : " "}
          InputProps={passwordInputProps}
        />

        {!passwordChanged && (
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : null
            }
            sx={{ mt: 2, py: 1.3 }}
          >
            {isLoading ? "Alterando..." : "Trocar senha"}
          </Button>
        )}

        {feedback && (
          <Alert
            severity={feedback.severity === "success" ? "success" : "error"}
            sx={{ mt: 3 }}
          >
            {feedback.message}
          </Alert>
        )}

        {passwordChanged ? (
          <Button
            component={RouterLink}
            to="/"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, py: 1.3 }}
          >
            Voltar para o login
          </Button>
        ) : (
          <Box sx={{ textAlign: "center", mt: 2.5 }}>
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              variant="body2"
              color="text.secondary"
            >
              Voltar para o login
            </Link>
          </Box>
        )}
      </form>
    </AuthLayout>
  );
}
